import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export type StepStatus = 'pending' | 'current' | 'completed' | 'error';
export type StepperOrientation = 'horizontal' | 'vertical';
export type StepperSize = 'sm' | 'md' | 'lg';
export type StepIndicatorVariant = 'circle' | 'dot' | 'number' | 'icon';

export interface StepItem {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  optional?: boolean;
}

export interface StepsContextValue {
  activeStep: number;
  orientation: StepperOrientation;
  size: StepperSize;
  variant: StepIndicatorVariant;
  clickable: boolean;
  goToStep: (step: number) => void;
  getStepStatus: (index: number) => StepStatus;
}

export interface StepsProps {
  /** Current active step (0-indexed) */
  activeStep: number;
  /** Callback when step changes */
  onStepChange?: (step: number) => void;
  /** Orientation of the steps */
  orientation?: StepperOrientation;
  /** Size of the step indicators */
  size?: StepperSize;
  /** Style variant for step indicators */
  variant?: StepIndicatorVariant;
  /** Allow clicking on completed steps */
  clickable?: boolean;
  /** Children step items */
  children: React.ReactNode;
  /** Additional class names */
  className?: string;
}

export interface StepProps {
  /** Step title */
  title: string;
  /** Step description */
  description?: string;
  /** Custom icon */
  icon?: React.ReactNode;
  /** Mark step as optional */
  optional?: boolean;
  /** Mark step as having an error */
  hasError?: boolean;
  /** Children content (for content panels) */
  children?: React.ReactNode;
  /** Additional class names */
  className?: string;
}

export interface StepIndicatorProps {
  /** Current status of the step */
  status: StepStatus;
  /** Step number (1-indexed for display) */
  stepNumber: number;
  /** Size */
  size?: StepperSize;
  /** Variant */
  variant?: StepIndicatorVariant;
  /** Custom icon for completed state */
  completedIcon?: React.ReactNode;
  /** Custom icon for current state */
  currentIcon?: React.ReactNode;
  /** Custom icon for error state */
  errorIcon?: React.ReactNode;
  /** Additional class names */
  className?: string;
}

export interface StepConnectorProps {
  /** Whether the connector is active (leading to current step) */
  active?: boolean;
  /** Whether the connector is completed */
  completed?: boolean;
  /** Orientation */
  orientation?: StepperOrientation;
  /** Additional class names */
  className?: string;
}

export interface StepperProps {
  /** Steps configuration */
  steps: StepItem[];
  /** Current active step */
  activeStep: number;
  /** Callback when step changes */
  onStepChange?: (step: number) => void;
  /** Orientation */
  orientation?: StepperOrientation;
  /** Size */
  size?: StepperSize;
  /** Variant */
  variant?: StepIndicatorVariant;
  /** Allow clicking on steps */
  clickable?: boolean;
  /** Show connector lines */
  showConnector?: boolean;
  /** Additional class names */
  className?: string;
}

export interface ProgressStepsProps {
  /** Total number of steps */
  totalSteps: number;
  /** Current step (1-indexed) */
  currentStep: number;
  /** Labels for each step */
  labels?: string[];
  /** Size */
  size?: StepperSize;
  /** Show step numbers */
  showNumbers?: boolean;
  /** Additional class names */
  className?: string;
}

export interface WizardProps {
  /** Steps configuration */
  steps: StepItem[];
  /** Current active step */
  activeStep: number;
  /** Callback when step changes */
  onStepChange?: (step: number) => void;
  /** Callback when wizard completes */
  onComplete?: () => void;
  /** Allow navigation to previous steps */
  allowBackNavigation?: boolean;
  /** Validate current step before proceeding */
  validateStep?: (step: number) => boolean | Promise<boolean>;
  /** Render function for step content */
  renderStepContent?: (step: number, item: StepItem) => React.ReactNode;
  /** Additional class names */
  className?: string;
}

export interface UseStepsOptions {
  /** Total number of steps */
  totalSteps: number;
  /** Initial step */
  initialStep?: number;
  /** Whether steps can be skipped */
  allowSkip?: boolean;
  /** Callback on step change */
  onStepChange?: (step: number) => void;
}

export interface UseStepsReturn {
  /** Current step (0-indexed) */
  currentStep: number;
  /** Total number of steps */
  totalSteps: number;
  /** Go to next step */
  nextStep: () => void;
  /** Go to previous step */
  prevStep: () => void;
  /** Go to specific step */
  goToStep: (step: number) => void;
  /** Reset to first step */
  reset: () => void;
  /** Whether on first step */
  isFirstStep: boolean;
  /** Whether on last step */
  isLastStep: boolean;
  /** Progress percentage (0-100) */
  progress: number;
  /** Get status of a step */
  getStepStatus: (index: number) => StepStatus;
}

// ============================================================================
// Context
// ============================================================================

const StepsContext = React.createContext<StepsContextValue | null>(null);

function useStepsContext(): StepsContextValue {
  const context = React.useContext(StepsContext);
  if (!context) {
    throw new Error('Step must be used within a Steps component');
  }
  return context;
}

// ============================================================================
// Hook: useSteps
// ============================================================================

export function useSteps(options: UseStepsOptions): UseStepsReturn {
  const { totalSteps, initialStep = 0, allowSkip = false, onStepChange } = options;

  const [currentStep, setCurrentStep] = React.useState(initialStep);

  const nextStep = React.useCallback(() => {
    setCurrentStep((prev) => {
      const next = Math.min(prev + 1, totalSteps - 1);
      if (next !== prev) {
        onStepChange?.(next);
      }
      return next;
    });
  }, [totalSteps, onStepChange]);

  const prevStep = React.useCallback(() => {
    setCurrentStep((prev) => {
      const next = Math.max(prev - 1, 0);
      if (next !== prev) {
        onStepChange?.(next);
      }
      return next;
    });
  }, [onStepChange]);

  const goToStep = React.useCallback((step: number) => {
    if (!allowSkip && step > currentStep + 1) {
      return;
    }
    const validStep = Math.max(0, Math.min(step, totalSteps - 1));
    if (validStep !== currentStep) {
      setCurrentStep(validStep);
      onStepChange?.(validStep);
    }
  }, [allowSkip, currentStep, totalSteps, onStepChange]);

  const reset = React.useCallback(() => {
    setCurrentStep(0);
    onStepChange?.(0);
  }, [onStepChange]);

  const getStepStatus = React.useCallback((index: number): StepStatus => {
    if (index < currentStep) return 'completed';
    if (index === currentStep) return 'current';
    return 'pending';
  }, [currentStep]);

  return {
    currentStep,
    totalSteps,
    nextStep,
    prevStep,
    goToStep,
    reset,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === totalSteps - 1,
    progress: totalSteps > 1 ? (currentStep / (totalSteps - 1)) * 100 : 100,
    getStepStatus,
  };
}

// ============================================================================
// Style Utilities
// ============================================================================

const sizeStyles: Record<StepperSize, { indicator: string; text: string; connector: string }> = {
  sm: { indicator: 'w-6 h-6 text-xs', text: 'text-xs', connector: 'h-0.5' },
  md: { indicator: 'w-8 h-8 text-sm', text: 'text-sm', connector: 'h-0.5' },
  lg: { indicator: 'w-10 h-10 text-base', text: 'text-base', connector: 'h-1' },
};

const dotSizes: Record<StepperSize, string> = {
  sm: 'w-2.5 h-2.5',
  md: 'w-3 h-3',
  lg: 'w-4 h-4',
};

function getStatusStyles(status: StepStatus): { bg: string; text: string; border: string } {
  switch (status) {
    case 'completed':
      return {
        bg: 'bg-green-500 dark:bg-green-600',
        text: 'text-white',
        border: 'border-green-500 dark:border-green-600',
      };
    case 'current':
      return {
        bg: 'bg-blue-500 dark:bg-blue-600',
        text: 'text-white',
        border: 'border-blue-500 dark:border-blue-600',
      };
    case 'error':
      return {
        bg: 'bg-red-500 dark:bg-red-600',
        text: 'text-white',
        border: 'border-red-500 dark:border-red-600',
      };
    default:
      return {
        bg: 'bg-gray-200 dark:bg-gray-700',
        text: 'text-gray-600 dark:text-gray-400',
        border: 'border-gray-300 dark:border-gray-600',
      };
  }
}

// ============================================================================
// Icons
// ============================================================================

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ErrorIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// ============================================================================
// StepIndicator Component
// ============================================================================

export function StepIndicator({
  status,
  stepNumber,
  size = 'md',
  variant = 'number',
  completedIcon,
  currentIcon,
  errorIcon,
  className,
}: StepIndicatorProps): React.ReactElement {
  const styles = getStatusStyles(status);
  const sizes = sizeStyles[size];

  const renderContent = () => {
    if (status === 'completed') {
      return completedIcon || <CheckIcon className="w-1/2 h-1/2" />;
    }
    if (status === 'error') {
      return errorIcon || <ErrorIcon className="w-1/2 h-1/2" />;
    }
    if (status === 'current' && currentIcon) {
      return currentIcon;
    }

    switch (variant) {
      case 'dot':
        return <span className={clsx('rounded-full', dotSizes[size], styles.bg)} />;
      case 'icon':
        return currentIcon || <span>{stepNumber}</span>;
      case 'circle':
        return null;
      default:
        return <span>{stepNumber}</span>;
    }
  };

  return (
    <div
      className={clsx(
        'flex items-center justify-center rounded-full font-medium',
        'transition-colors duration-200',
        sizes.indicator,
        variant === 'dot' && status === 'pending' && 'bg-transparent border-2 border-gray-300 dark:border-gray-600',
        variant !== 'dot' && styles.bg,
        styles.text,
        className
      )}
      aria-current={status === 'current' ? 'step' : undefined}
    >
      {renderContent()}
    </div>
  );
}

// ============================================================================
// StepConnector Component
// ============================================================================

export function StepConnector({
  active = false,
  completed = false,
  orientation = 'horizontal',
  className,
}: StepConnectorProps): React.ReactElement {
  return (
    <div
      className={clsx(
        'transition-colors duration-200',
        orientation === 'horizontal' ? 'flex-1 h-0.5 mx-2' : 'w-0.5 h-full my-2',
        completed || active ? 'bg-green-500 dark:bg-green-600' : 'bg-gray-200 dark:bg-gray-700',
        className
      )}
      aria-hidden="true"
    />
  );
}

// ============================================================================
// Step Component
// ============================================================================

export function Step({
  title,
  description,
  icon,
  optional = false,
  hasError = false,
  children,
  className,
}: StepProps): React.ReactElement {
  return (
    <div className={clsx('flex-1', className)}>
      <div className="flex flex-col items-center text-center">
        <span className="font-medium text-gray-900 dark:text-gray-100">{title}</span>
        {description && (
          <span className="text-sm text-gray-500 dark:text-gray-400">{description}</span>
        )}
        {optional && (
          <span className="text-xs text-gray-400 dark:text-gray-500">Optional</span>
        )}
      </div>
      {children}
    </div>
  );
}

// ============================================================================
// Steps Component (Compound)
// ============================================================================

export function Steps({
  activeStep,
  onStepChange,
  orientation = 'horizontal',
  size = 'md',
  variant = 'number',
  clickable = false,
  children,
  className,
}: StepsProps): React.ReactElement {
  const goToStep = React.useCallback((step: number) => {
    onStepChange?.(step);
  }, [onStepChange]);

  const getStepStatus = React.useCallback((index: number): StepStatus => {
    if (index < activeStep) return 'completed';
    if (index === activeStep) return 'current';
    return 'pending';
  }, [activeStep]);

  const contextValue = React.useMemo(() => ({
    activeStep,
    orientation,
    size,
    variant,
    clickable,
    goToStep,
    getStepStatus,
  }), [activeStep, orientation, size, variant, clickable, goToStep, getStepStatus]);

  return (
    <StepsContext.Provider value={contextValue}>
      <div
        className={clsx(
          'flex',
          orientation === 'vertical' ? 'flex-col gap-4' : 'flex-row items-center gap-2',
          className
        )}
        role="navigation"
        aria-label="Progress steps"
      >
        {children}
      </div>
    </StepsContext.Provider>
  );
}

// ============================================================================
// Stepper Component
// ============================================================================

export function Stepper({
  steps,
  activeStep,
  onStepChange,
  orientation = 'horizontal',
  size = 'md',
  variant = 'number',
  clickable = false,
  showConnector = true,
  className,
}: StepperProps): React.ReactElement {
  const handleStepClick = (index: number) => {
    if (clickable && index <= activeStep) {
      onStepChange?.(index);
    }
  };

  const getStatus = (index: number): StepStatus => {
    if (index < activeStep) return 'completed';
    if (index === activeStep) return 'current';
    return 'pending';
  };

  return (
    <div
      className={clsx(
        'flex',
        orientation === 'vertical' ? 'flex-col' : 'flex-row items-start',
        className
      )}
      role="navigation"
      aria-label="Progress steps"
    >
      {steps.map((step, index) => {
        const status = getStatus(index);
        const isLast = index === steps.length - 1;

        return (
          <React.Fragment key={step.id}>
            <div
              className={clsx(
                'flex',
                orientation === 'vertical' ? 'flex-row items-start gap-3' : 'flex-col items-center gap-2',
                clickable && index <= activeStep && 'cursor-pointer',
                orientation === 'horizontal' && 'flex-1'
              )}
              onClick={() => handleStepClick(index)}
              role={clickable && index <= activeStep ? 'button' : undefined}
              tabIndex={clickable && index <= activeStep ? 0 : undefined}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleStepClick(index);
                }
              }}
            >
              <StepIndicator
                status={status}
                stepNumber={index + 1}
                size={size}
                variant={variant}
                currentIcon={step.icon}
              />
              <div className={clsx(
                'flex flex-col',
                orientation === 'horizontal' && 'items-center text-center'
              )}>
                <span className={clsx(
                  'font-medium',
                  sizeStyles[size].text,
                  status === 'current' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'
                )}>
                  {step.title}
                </span>
                {step.description && (
                  <span className={clsx('text-gray-500 dark:text-gray-400', sizeStyles[size].text)}>
                    {step.description}
                  </span>
                )}
                {step.optional && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">Optional</span>
                )}
              </div>
            </div>
            {showConnector && !isLast && (
              <StepConnector
                completed={index < activeStep}
                active={index === activeStep - 1}
                orientation={orientation}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ============================================================================
// ProgressSteps Component
// ============================================================================

export function ProgressSteps({
  totalSteps,
  currentStep,
  labels,
  size = 'md',
  showNumbers = true,
  className,
}: ProgressStepsProps): React.ReactElement {
  const progress = ((currentStep - 1) / (totalSteps - 1)) * 100;

  return (
    <div className={clsx('w-full', className)}>
      {/* Progress bar container */}
      <div className="relative flex items-center justify-between">
        {/* Background line */}
        <div className="absolute left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700" />
        {/* Active progress line */}
        <div
          className="absolute left-0 h-1 bg-blue-500 dark:bg-blue-600 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />

        {/* Step indicators */}
        {Array.from({ length: totalSteps }, (_, i) => {
          const stepNum = i + 1;
          const isCompleted = stepNum < currentStep;
          const isCurrent = stepNum === currentStep;

          return (
            <div key={i} className="relative z-10 flex flex-col items-center">
              <div
                className={clsx(
                  'flex items-center justify-center rounded-full font-medium transition-colors duration-200',
                  sizeStyles[size].indicator,
                  isCompleted && 'bg-blue-500 text-white dark:bg-blue-600',
                  isCurrent && 'bg-blue-500 text-white dark:bg-blue-600 ring-4 ring-blue-100 dark:ring-blue-900',
                  !isCompleted && !isCurrent && 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                )}
              >
                {isCompleted ? <CheckIcon className="w-1/2 h-1/2" /> : showNumbers && stepNum}
              </div>
              {labels?.[i] && (
                <span className={clsx(
                  'mt-2 text-center whitespace-nowrap',
                  sizeStyles[size].text,
                  isCurrent ? 'text-blue-600 font-medium dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                )}>
                  {labels[i]}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Wizard Component
// ============================================================================

export function Wizard({
  steps,
  activeStep,
  onStepChange,
  onComplete,
  allowBackNavigation = true,
  validateStep,
  renderStepContent,
  className,
}: WizardProps): React.ReactElement {
  const [isValidating, setIsValidating] = React.useState(false);

  const handleNext = async () => {
    if (validateStep) {
      setIsValidating(true);
      const isValid = await validateStep(activeStep);
      setIsValidating(false);
      if (!isValid) return;
    }

    if (activeStep === steps.length - 1) {
      onComplete?.();
    } else {
      onStepChange?.(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (allowBackNavigation && activeStep > 0) {
      onStepChange?.(activeStep - 1);
    }
  };

  const handleStepClick = (index: number) => {
    if (index < activeStep && allowBackNavigation) {
      onStepChange?.(index);
    }
  };

  return (
    <div className={clsx('flex flex-col gap-6', className)}>
      {/* Stepper navigation */}
      <Stepper
        steps={steps}
        activeStep={activeStep}
        onStepChange={handleStepClick}
        clickable={allowBackNavigation}
      />

      {/* Step content */}
      <div className="min-h-[200px]">
        {renderStepContent?.(activeStep, steps[activeStep])}
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={handleBack}
          disabled={activeStep === 0 || !allowBackNavigation}
          className={clsx(
            'px-4 py-2 text-sm font-medium rounded-md',
            'text-gray-700 bg-white border border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700',
            'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
          )}
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={isValidating}
          className={clsx(
            'px-4 py-2 text-sm font-medium rounded-md',
            'text-white bg-blue-600 border border-transparent',
            'hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'dark:bg-blue-500 dark:hover:bg-blue-600'
          )}
        >
          {isValidating ? 'Validating...' : activeStep === steps.length - 1 ? 'Complete' : 'Next'}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// SimpleSteps - Minimal Steps Display
// ============================================================================

export interface SimpleStepsProps {
  /** Current step (1-indexed) */
  currentStep: number;
  /** Total steps */
  totalSteps: number;
  /** Additional class names */
  className?: string;
}

export function SimpleSteps({
  currentStep,
  totalSteps,
  className,
}: SimpleStepsProps): React.ReactElement {
  return (
    <span className={clsx('text-sm text-gray-500 dark:text-gray-400', className)}>
      Step {currentStep} of {totalSteps}
    </span>
  );
}

// ============================================================================
// StepProgress - Progress Bar Style
// ============================================================================

export interface StepProgressProps {
  /** Current step (1-indexed) */
  currentStep: number;
  /** Total steps */
  totalSteps: number;
  /** Show percentage */
  showPercentage?: boolean;
  /** Additional class names */
  className?: string;
}

export function StepProgress({
  currentStep,
  totalSteps,
  showPercentage = false,
  className,
}: StepProgressProps): React.ReactElement {
  const progress = Math.round((currentStep / totalSteps) * 100);

  return (
    <div className={clsx('w-full', className)}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Step {currentStep} of {totalSteps}
        </span>
        {showPercentage && (
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {progress}%
          </span>
        )}
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full dark:bg-gray-700">
        <div
          className="h-full bg-blue-500 rounded-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

// ============================================================================
// BreadcrumbSteps - Breadcrumb Style Navigation
// ============================================================================

export interface BreadcrumbStepsProps {
  /** Steps configuration */
  steps: StepItem[];
  /** Current active step */
  activeStep: number;
  /** Callback when step changes */
  onStepChange?: (step: number) => void;
  /** Additional class names */
  className?: string;
}

export function BreadcrumbSteps({
  steps,
  activeStep,
  onStepChange,
  className,
}: BreadcrumbStepsProps): React.ReactElement {
  return (
    <nav className={clsx('flex items-center space-x-2', className)} aria-label="Steps">
      {steps.map((step, index) => {
        const isCompleted = index < activeStep;
        const isCurrent = index === activeStep;

        return (
          <React.Fragment key={step.id}>
            {index > 0 && (
              <svg
                className="w-4 h-4 text-gray-400 dark:text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
            <button
              type="button"
              onClick={() => isCompleted && onStepChange?.(index)}
              disabled={!isCompleted}
              className={clsx(
                'text-sm font-medium transition-colors',
                isCompleted && 'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer',
                isCurrent && 'text-gray-900 dark:text-gray-100',
                !isCompleted && !isCurrent && 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
              )}
              aria-current={isCurrent ? 'step' : undefined}
            >
              {step.title}
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  );
}

// ============================================================================
// VerticalStepper - Vertical Layout with Content
// ============================================================================

export interface VerticalStepperProps {
  /** Steps configuration */
  steps: StepItem[];
  /** Current active step */
  activeStep: number;
  /** Render function for step content */
  renderContent?: (step: number, item: StepItem) => React.ReactNode;
  /** Size */
  size?: StepperSize;
  /** Additional class names */
  className?: string;
}

export function VerticalStepper({
  steps,
  activeStep,
  renderContent,
  size = 'md',
  className,
}: VerticalStepperProps): React.ReactElement {
  const getStatus = (index: number): StepStatus => {
    if (index < activeStep) return 'completed';
    if (index === activeStep) return 'current';
    return 'pending';
  };

  return (
    <div className={clsx('flex flex-col', className)}>
      {steps.map((step, index) => {
        const status = getStatus(index);
        const isLast = index === steps.length - 1;

        return (
          <div key={step.id} className="flex gap-4">
            {/* Indicator column */}
            <div className="flex flex-col items-center">
              <StepIndicator
                status={status}
                stepNumber={index + 1}
                size={size}
              />
              {!isLast && (
                <div
                  className={clsx(
                    'w-0.5 flex-1 mt-2',
                    index < activeStep ? 'bg-green-500 dark:bg-green-600' : 'bg-gray-200 dark:bg-gray-700'
                  )}
                />
              )}
            </div>

            {/* Content column */}
            <div className={clsx('flex-1 pb-8', isLast && 'pb-0')}>
              <h3 className={clsx(
                'font-medium',
                sizeStyles[size].text,
                status === 'current' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'
              )}>
                {step.title}
              </h3>
              {step.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {step.description}
                </p>
              )}
              {status === 'current' && renderContent && (
                <div className="mt-4">
                  {renderContent(index, step)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
